import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Star, ShieldCheck, MapPin, Briefcase, Calendar, CurrencyDollar,
  Hammer, ChatCircleDots, ShareNetwork, CheckCircle, Warning,
  CaretLeft, Image as ImageIcon, Clock, Copy, Lightning, Medal
} from '@phosphor-icons/react';
import axiosInstance from '../api/api';
import { toast } from '../hooks/use-toast';
import { withUtm } from '../utils/analytics';
import VerifiedBadges from '../components/VerifiedBadges';
import FastResponderBadge from '../components/FastResponderBadge';
import PublicAvailabilityCalendar from '../components/PublicAvailabilityCalendar';

const Stars = ({ rating, size = 18 }) => {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${r.toFixed(1)} out of 5`}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          weight={i <= Math.round(r) ? 'fill' : 'regular'}
          className={i <= Math.round(r) ? 'text-amber-500' : 'text-neutral-300 dark:text-neutral-700'}
        />
      ))}
    </span>
  );
};

const PublicContractorProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await axiosInstance.get(`/profiles/${encodeURIComponent(slug)}`);
        if (!cancelled) {
          setData(resp.data);
          const p = resp.data.profile || {};
          const name = p.name || 'Pro';
          const trade = p.trade || 'Construction Pro';
          const location = p.location || '';
          const rating = Number(p.rating || 0);
          const reviews = Number(p.reviews_count || 0);
          const verifiedBadge = p.verified ? ' · Verified Pro ✓' : '';
          const stars = reviews ? ` ★ ${rating.toFixed(1)}/5 (${reviews} reviews)` : '';
          const title = `${name} — ${trade}${location ? ' — ' + location : ''} | ConstructHelp`;
          const description = `${(p.bio || '').slice(0, 140) || `${trade} on ConstructHelp${verifiedBadge}${stars}.`}`;
          const ogUrl = `${window.location.origin}/api/profiles/og/${slug}`;
          const img = p.avatar_url || `${window.location.origin}/og-default.png`;
          document.title = title;
          // Client-side OG/Twitter tags — backup for JS-aware crawlers + browser tab info
          const upsert = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
              el = document.createElement('meta');
              Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
              document.head.appendChild(el);
            } else {
              Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            }
          };
          upsert('meta[name="description"]', { name: 'description', content: description });
          upsert('meta[property="og:type"]', { property: 'og:type', content: 'profile' });
          upsert('meta[property="og:site_name"]', { property: 'og:site_name', content: 'ConstructHelp' });
          upsert('meta[property="og:title"]', { property: 'og:title', content: title });
          upsert('meta[property="og:description"]', { property: 'og:description', content: description });
          upsert('meta[property="og:url"]', { property: 'og:url', content: ogUrl });
          upsert('meta[property="og:image"]', { property: 'og:image', content: img });
          upsert('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
          upsert('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
          upsert('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
          upsert('meta[name="twitter:image"]', { name: 'twitter:image', content: img });
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? 'not-found' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  const handleShare = async () => {
    // Use the OG-tagged share URL so recipients get rich previews in chats/socials.
    const baseUrl = `${window.location.origin}/api/profiles/og/${slug}`;
    const name = data?.profile?.name || 'this pro';
    if (navigator.share) {
      try { await navigator.share({ title: `${name} on ConstructHelp`, url: withUtm(baseUrl, { medium: 'webshare', campaign: 'profile_share' }) }); return; } catch {}
    }
    const url = withUtm(baseUrl, { medium: 'copy_link', campaign: 'profile_share' });
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: 'Profile URL copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: url, variant: 'destructive' });
    }
  };

  const handleHire = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate(`/signup?intent=hire&pro=${slug}`);
      return;
    }
    // Send to post-job pre-filled with this pro id
    navigate(`/post-job?invite_user=${data?.profile?.id || ''}`);
  };

  const handleMessage = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate(`/login?next=/contractor/${slug}`);
      return;
    }
    navigate(`/messages?to=${data?.profile?.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950" data-testid="profile-loading">
        <Hammer size={32} weight="bold" className="text-[#D95800] animate-pulse" />
      </div>
    );
  }

  if (error === 'not-found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 p-6" data-testid="profile-not-found">
        <div className="max-w-md text-center">
          <Warning size={48} weight="bold" className="text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-2">Profile not found</h1>
          <p className="text-neutral-500 mb-6">This contractor profile doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/')} className="h-12 px-6 bg-[#D95800] text-white font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000]">Return Home</button>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 p-6">
        <p className="text-neutral-500">Something went wrong loading this profile.</p>
      </div>
    );
  }

  const p = data.profile;
  const reviews = data.reviews || [];
  const portfolio = data.portfolio || [];
  const reviewPhotoBase = `${process.env.REACT_APP_BACKEND_URL}/api/reviews/photo/`;

  const initials = (p.name || 'Pro').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950" data-testid="public-profile">
      {/* Top bar */}
      <header className="bg-white dark:bg-neutral-900 border-b-2 border-black sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold text-neutral-700 dark:text-white hover:text-[#D95800]">
            <CaretLeft size={18} weight="bold" /> ConstructHelp
          </Link>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 h-9 px-3 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-white font-bold text-sm rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
            data-testid="share-profile-btn"
          >
            <ShareNetwork size={16} weight="bold" /> Share
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1A1A1A] via-neutral-900 to-[#2D2D2D] text-white border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-10 md:py-14">
          <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-start">
            {/* Avatar */}
            <div className="w-24 h-24 md:w-28 md:h-28 bg-[#D95800] rounded-2xl border-4 border-white flex items-center justify-center text-3xl md:text-4xl font-black shadow-[4px_4px_0px_#000]" data-testid="profile-avatar">
              {p.avatar_url ? (
                <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover rounded-2xl" />
              ) : (
                <span>{initials}</span>
              )}
            </div>

            {/* Identity */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h1 className="text-3xl md:text-4xl font-black leading-tight" data-testid="profile-name">{p.name}</h1>
                {p.verified && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest rounded border-2 border-black"
                    data-testid="verified-badge"
                  >
                    <ShieldCheck size={12} weight="fill" /> Verified Pro
                  </span>
                )}
                {p.fast_responder && <FastResponderBadge size="md" />}
              </div>
              {/* Granular trust badges (Licensed · Insured · ID) */}
              <div className="mb-3">
                <VerifiedBadges
                  licensed={!!p.verified}
                  insured={!!p.verified}
                  idVerified={!!(p.id_verified || p.verified)}
                  testIdPrefix="profile-trust"
                />
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-300">
                {p.trade && <span className="flex items-center gap-1"><Hammer size={14} weight="bold" className="text-[#D95800]" /> {p.trade}</span>}
                {p.location && <span className="flex items-center gap-1"><MapPin size={14} weight="bold" className="text-[#D95800]" /> {p.location}</span>}
                {p.user_type && <span className="text-[10px] font-black uppercase tracking-widest text-neutral-500">{p.user_type}</span>}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Stars rating={p.rating} />
                  <span className="text-sm font-bold">{(p.rating || 0).toFixed(1)}</span>
                  <span className="text-xs text-neutral-400">({p.reviews_count} reviews)</span>
                </div>
              </div>
              {p.bio && (
                <p className="mt-4 text-sm text-neutral-300 leading-relaxed max-w-2xl">{p.bio}</p>
              )}
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-2 md:w-44">
              <button
                onClick={handleHire}
                className="h-12 px-5 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase tracking-wider text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
                data-testid="hire-me-btn"
              >
                Hire Me
              </button>
              <button
                onClick={handleMessage}
                className="h-12 px-5 bg-white text-neutral-900 font-black uppercase tracking-wider text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center justify-center gap-2"
                data-testid="message-btn"
              >
                <ChatCircleDots size={16} weight="bold" /> Message
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white dark:bg-neutral-900 border-b-2 border-black">
        <div className="max-w-5xl mx-auto px-4 py-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="stats-strip">
          <StatBox icon={CheckCircle} label="Jobs Completed" value={p.completed_jobs ?? 0} />
          <StatBox icon={Star} label="Avg Rating" value={(p.rating || 0).toFixed(1)} />
          <StatBox icon={Calendar} label="On Platform" value={p.years_on_platform != null ? `${p.years_on_platform}y` : '—'} />
          <StatBox icon={CurrencyDollar} label="Hourly Rate" value={p.hourly_rate || '—'} />
          <StatBox icon={Lightning} label="Responds In" value={p.response_time || '—'} />
          <StatBox icon={Medal} label="Completion Rate" value={p.completion_rate != null ? `${p.completion_rate}%` : '—'} />
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-8 grid md:grid-cols-3 gap-6">
        {/* Left: bio + portfolio */}
        <div className="md:col-span-2 space-y-6">
          {portfolio.length > 0 && (
            <section className="bg-white dark:bg-neutral-900 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]" data-testid="portfolio-section">
              <h2 className="font-black text-lg uppercase tracking-wider mb-4 flex items-center gap-2">
                <ImageIcon size={20} weight="bold" className="text-[#D95800]" /> Recent Work
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {portfolio.map((file_id) => (
                  <a
                    key={file_id}
                    href={`${reviewPhotoBase}${file_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block aspect-square border-2 border-black bg-neutral-100 dark:bg-neutral-800 overflow-hidden hover:opacity-90 transition-opacity"
                    data-testid={`portfolio-photo-${file_id}`}
                  >
                    <img src={`${reviewPhotoBase}${file_id}`} alt="Work" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Availability Calendar */}
          <PublicAvailabilityCalendar userId={p.id} userName={p.name} />

          {/* Reviews */}
          <section className="bg-white dark:bg-neutral-900 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]" data-testid="reviews-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-black text-lg uppercase tracking-wider flex items-center gap-2">
                <Star size={20} weight="fill" className="text-amber-500" /> Reviews
              </h2>
              <span className="text-xs font-bold text-neutral-500">{p.reviews_count} total</span>
            </div>
            {reviews.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <p className="text-sm">No reviews yet.</p>
              </div>
            ) : (
              <ul className="space-y-4">
                {reviews.map(r => (
                  <li key={r.id} className="border-l-4 border-[#D95800] pl-4 py-2" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Stars rating={r.rating} size={14} />
                      <span className="font-bold text-sm">{r.reviewer_name}</span>
                      {r.verified && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-white px-1.5 py-0.5 rounded border-2 border-black shadow-[2px_2px_0px_#000]" data-testid="review-verified-hire">
                          <CheckCircle size={10} weight="fill" /> Verified Hire
                        </span>
                      )}
                    </div>
                    {r.text && <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{r.text}</p>}
                    {r.photos?.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {r.photos.map(pid => (
                          <a key={pid} href={`${reviewPhotoBase}${pid}`} target="_blank" rel="noreferrer" className="w-16 h-16 border-2 border-black bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                            <img src={`${reviewPhotoBase}${pid}`} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Right: meta panel */}
        <aside className="space-y-4">
          <div className="bg-white dark:bg-neutral-900 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]">
            <h3 className="font-black text-xs uppercase tracking-widest text-neutral-500 mb-3">Details</h3>
            <ul className="space-y-3 text-sm">
              {p.experience && (
                <li className="flex items-start gap-2"><Briefcase size={16} weight="bold" className="text-[#2B4C6F] mt-0.5" /><div><span className="font-bold">Experience</span><br/><span className="text-neutral-600 dark:text-neutral-400">{p.experience}</span></div></li>
              )}
              {p.availability && (
                <li className="flex items-start gap-2"><Clock size={16} weight="bold" className="text-[#2B4C6F] mt-0.5" /><div><span className="font-bold">Availability</span><br/><span className="text-neutral-600 dark:text-neutral-400">{p.availability}</span></div></li>
              )}
              {p.member_since && (
                <li className="flex items-start gap-2"><Calendar size={16} weight="bold" className="text-[#2B4C6F] mt-0.5" /><div><span className="font-bold">Member Since</span><br/><span className="text-neutral-600 dark:text-neutral-400">{new Date(p.member_since).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span></div></li>
              )}
            </ul>
          </div>

          <div className="bg-[#D95800]/10 dark:bg-[#D95800]/20 border-2 border-[#D95800] rounded-xl p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#D95800] mb-2">Share this pro</p>
            <button
              onClick={handleShare}
              className="w-full h-10 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white font-bold text-sm rounded-lg border-2 border-black flex items-center justify-center gap-2"
              data-testid="share-bottom-btn"
            >
              <Copy size={14} weight="bold" /> Copy Link
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

const StatBox = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-800 border-2 border-black rounded-lg">
    <div className="w-10 h-10 bg-[#D95800]/10 border-2 border-[#D95800] rounded-lg flex items-center justify-center">
      <Icon size={20} weight="bold" className="text-[#D95800]" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="font-black text-lg leading-tight truncate">{value}</p>
    </div>
  </div>
);

export default PublicContractorProfile;
